export interface KeyedItem<T> {
  key: string;
  item: T;
  index: number;
}

/**
 * Pairs every item with a precomputed React key.
 *
 * Use this for lists whose items have no unique identifier of their own, so the key is derived
 * once up front instead of from the index inside the rendering `.map()` callback.
 *
 * @param items - The items to render.
 * @param getKey - Builds the key for an item. Must return a value that is unique within `items`.
 * @returns The items wrapped together with their key and original index, in the original order.
 */
export function withKeys<T>(items: readonly T[], getKey: (item: T, index: number) => string): KeyedItem<T>[] {
  return items.map((item, index) => ({ key: getKey(item, index), item, index }));
}

/**
 * Creates unique, stable keys for rendering a fixed number of placeholder elements
 * that have no identity of their own.
 *
 * @param count - The number of keys to create.
 * @param prefix - The prefix of each key.
 * @returns The keys `${prefix}-0` up to `${prefix}-${count - 1}`.
 */
export function createPlaceholderKeys(count: number, prefix: string): string[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => `${prefix}-${index}`);
}
